import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  PrintOutlined as PrintAllIcon,
  School as SchoolIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';
import { API_URL } from '../utils/api';
import { generateUsernameFromName } from '../utils/usernameGenerator';

const Teachers = () => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, teacher: null });
  const [loginAccount, setLoginAccount] = useState({ open: false, teacher: null, loading: false, generated: null, existing: null, passwordInput: '' });
  const [formData, setFormData] = useState({
    nip: '',
    name: '',
    phone: '',
    gender: '',
  });
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { colors, mode } = useThemeContext();

  useEffect(() => {
    loadTeachers();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadTeachers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('Token autentikasi tidak ditemukan', 'error');
        return;
      }
      const response = await fetch(`${API_URL}/api/employees?person_type=teacher`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Gagal memuat data guru');
      const result = await response.json();
      if (result.success) setTeachers(result.data);
      else showSnackbar(result.message || 'Gagal memuat data guru', 'error');
    } catch (error) {
      showSnackbar(error.message || 'Gagal memuat data guru', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (teacher = null) => {
    if (teacher) {
      setEditing(teacher);
      setFormData({
        nip: teacher.student_id || '',
        name: teacher.full_name || '',
        phone: teacher.phone || '',
        gender: teacher.gender || '',
      });
    } else {
      setEditing(null);
      setFormData({ nip: '', name: '', phone: '', gender: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditing(null);
  };

  const handleOpenLoginAccount = async (teacher) => {
    setLoginAccount({ open: true, teacher, loading: true, generated: null, existing: null, passwordInput: '' });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/employees/${teacher.id}/login-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setLoginAccount((prev) => ({
          ...prev,
          loading: false,
          existing: result.data,
          passwordInput: ''
        }));
        return;
      }
      throw new Error(result.error || 'Gagal memuat akun login');
    } catch (error) {
      showSnackbar(error.message || 'Gagal memuat akun login', 'error');
      setLoginAccount({ open: true, teacher, loading: false, generated: null, existing: null, passwordInput: '' });
    }
  };

  const handleCloseLoginAccount = () => {
    setLoginAccount({ open: false, teacher: null, loading: false, generated: null, existing: null, passwordInput: '' });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Disalin ke clipboard', 'success');
  };

  const handleCreateLoginAccount = async () => {
    if (!loginAccount.teacher) return;
    setLoginAccount((prev) => ({ ...prev, loading: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/employees/${loginAccount.teacher.id}/create-login`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: generateUsernameFromName(loginAccount.teacher.full_name),
          password: 'guru123',
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Gagal membuat akun login');
      }

      setLoginAccount((prev) => ({
        ...prev,
        loading: false,
        existing: {
          ...result.data,
          password: loginAccount.passwordInput.trim() || 'guru123',
        },
      }));
      showSnackbar(`Akun login dibuat: ${result.data.username}`, 'success');
      await loadTeachers();
    } catch (error) {
      setLoginAccount((prev) => ({ ...prev, loading: false }));
      showSnackbar(error.message || 'Gagal membuat akun login', 'error');
    }
  };

  const handleResetPassword = async () => {
    if (!loginAccount.teacher) return;
    if (!loginAccount.passwordInput.trim()) {
      showSnackbar('Password baru wajib diisi', 'error');
      return;
    }
    setLoginAccount((prev) => ({ ...prev, loading: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/employees/${loginAccount.teacher.id}/reset-password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: loginAccount.passwordInput.trim() }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Gagal reset password');
      }

      setLoginAccount((prev) => ({
        ...prev,
        loading: false,
        existing: {
          ...result.data,
          password: loginAccount.passwordInput.trim(),
        },
      }));
      showSnackbar(`Password direset untuk ${result.data.username}`, 'success');
    } catch (error) {
      setLoginAccount((prev) => ({ ...prev, loading: false }));
      showSnackbar(error.message || 'Gagal reset password', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        student_id: formData.nip.trim(),
        full_name: formData.name.trim(),
        person_type: 'teacher',
        hire_date: new Date().toISOString().split('T')[0],
      };
      if (formData.phone.trim()) payload.phone = formData.phone.trim();
      if (formData.gender) payload.gender = formData.gender;

      const url = editing
        ? `${API_URL}/api/employees/${editing.id}`
        : `${API_URL}/api/employees`;
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || result.message || 'Gagal menyimpan');
      showSnackbar(editing ? 'Guru diperbarui' : 'Guru ditambahkan', 'success');
      loadTeachers();
      handleCloseDialog();
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.teacher) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/employees/${deleteConfirm.teacher.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Gagal menghapus');
      showSnackbar('Guru dihapus', 'success');
      loadTeachers();
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setDeleteConfirm({ open: false, teacher: null });
      setLoading(false);
    }
  };

  const filtered = teachers.filter((t) => {
    const q = searchTerm.toLowerCase();
    return (
      t.full_name?.toLowerCase().includes(q) ||
      t.student_id?.toLowerCase().includes(q) ||
      t.phone?.toLowerCase().includes(q)
    );
  });

  const getGenderText = (g) => (g === 'male' ? 'Laki-laki' : g === 'female' ? 'Perempuan' : '-');

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Data Guru
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Absensi QR sama dengan siswa · tidak ada notifikasi WhatsApp ke guru
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<PrintAllIcon />}
            onClick={() => navigate('/teachers/print-all')}
            disabled={teachers.length === 0}
          >
            Cetak Semua Kartu
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Tambah Guru
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 2, border: `1px solid ${colors.border}`, bgcolor: colors.card }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Cari nama, NIP, atau telepon..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadTeachers} disabled={loading}>
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ borderRadius: 2, bgcolor: colors.card, border: `1px solid ${colors.border}` }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: mode === 'dark' ? '#1f2937' : 'grey.50' }}>
              <TableCell>NIP</TableCell>
              <TableCell>Nama</TableCell>
              <TableCell>Telepon</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell align="right">Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && teachers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  Belum ada data guru
                </TableCell>
              </TableRow>
            ) : (
              filtered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.student_id}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{t.full_name}</TableCell>
                    <TableCell>{t.phone || '-'}</TableCell>
                    <TableCell>
                      <Chip label={getGenderText(t.gender)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Cetak kartu QR">
                        <IconButton size="small" color="primary" onClick={() => navigate(`/teachers/${t.id}/card`)}>
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                         <IconButton size="small" color="info" onClick={() => handleOpenDialog(t)}>
                           <EditIcon />
                         </IconButton>
                       </Tooltip>
                       <Tooltip title="Akun Login">
                         <IconButton size="small" color="secondary" onClick={() => handleOpenLoginAccount(t)}>
                           <SchoolIcon />
                         </IconButton>
                       </Tooltip>
                       <Tooltip title="Hapus">
                         <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ open: true, teacher: t })}>
                           <DeleteIcon />
                         </IconButton>
                       </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Baris per halaman"
        />
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon color="primary" />
          {editing ? 'Edit Guru' : 'Tambah Guru'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="NIP"
              value={formData.nip}
              onChange={(e) => setFormData({ ...formData, nip: e.target.value })}
              required
              disabled={!!editing}
              sx={{ mb: 2 }}
              helperText={editing ? 'NIP tidak dapat diubah' : 'ID unik untuk QR absensi guru'}
            />
            <TextField
              fullWidth
              label="Nama Lengkap"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Telepon (opsional)"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.gender}
                label="Gender"
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              >
                <MenuItem value="">-</MenuItem>
                <MenuItem value="male">Laki-laki</MenuItem>
                <MenuItem value="female">Perempuan</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Batal</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              Simpan
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, teacher: null })}>
        <DialogTitle>Hapus Guru?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deleteConfirm.teacher?.full_name} (NIP {deleteConfirm.teacher?.student_id})
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, teacher: null })}>Batal</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Hapus
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={loginAccount.open} onClose={handleCloseLoginAccount} maxWidth="sm" fullWidth>
        <DialogTitle>Buat Akun Login Guru</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {loginAccount.teacher?.full_name} (NIP {loginAccount.teacher?.student_id})
          </Typography>

          {loginAccount.existing ? (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 2, border: '1px solid', borderColor: 'primary.main' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'primary.dark' }}>
                Akun Login Guru
              </Typography>

              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Username
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField fullWidth value={loginAccount.existing.username} readOnly size="small" />
                  <Tooltip title="Salin username">
                    <IconButton size="small" onClick={() => copyToClipboard(loginAccount.existing.username)}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Password
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    value={loginAccount.existing.password || ''}
                    type="text"
                    readOnly
                    size="small"
                  />
                  <Tooltip title="Salin password">
                    <IconButton size="small" onClick={() => copyToClipboard(loginAccount.existing.password || '')}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mt: 2, mb: 0 }}>
                <strong>Catatan:</strong> Password bisa diubah manual lewat input reset password di bawah.
              </Alert>
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Klik tombol di bawah untuk membuat akun login guru.
            </Alert>
          )}

          {loginAccount.existing && (
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Password Baru"
                type="password"
                value={loginAccount.passwordInput}
                onChange={(e) => setLoginAccount((prev) => ({ ...prev, passwordInput: e.target.value }))}
                helperText="Isi password baru lalu klik Reset Password"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLoginAccount}>Tutup</Button>
          {!loginAccount.existing ? (
            <Button variant="contained" onClick={handleCreateLoginAccount} disabled={loginAccount.loading}>
              {loginAccount.loading ? 'Membuat...' : 'Buat Akun Login'}
            </Button>
          ) : (
            <>
              <Button variant="outlined" onClick={handleResetPassword} disabled={loginAccount.loading}>
                {loginAccount.loading ? 'Reset...' : 'Reset Password'}
              </Button>
              <Button variant="contained" onClick={handleCloseLoginAccount}>
                Selesai
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Teachers;
